/**
 * The "License summary" table.
 *
 * Rendered as divs rather than a <table>: it is a label/value list, not tabular
 * data with meaningful columns, so a table's row/column semantics would be noise.
 * The last row is special — it holds the verification URL and its copy button — so
 * it is written out rather than fed through SummaryRow.
 */
import { Copy } from 'lucide-react';
import { COMMERCIAL_ADDON } from '../../data/products';
import { c } from '../../styles/theme';

/** One label/value row. `last` drops the divider so the card ends cleanly. */
function SummaryRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 14px',
        borderBottom: last ? undefined : `1px solid ${c.border}`,
      }}
    >
      <span style={{ fontSize: 12, color: c.text3 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export interface LicenseSummaryTableProps {
  customerName: string;
  /** Human-formatted issue date, or "—". */
  issued: string;
  /** Human-formatted date the terms last changed, or "—". */
  updatedDate: string;
  /** Public verification URL shown to the owner and copied by the action beside it. */
  verifyUrl: string;
  status?: 'active' | 'revoked' | 'suspended' | 'refunded' | 'expired';
  onCopyLink: () => void;
}

function statusLabel(status: LicenseSummaryTableProps['status']): string {
  switch (status) {
    case 'revoked':
      return 'Revoked';
    case 'suspended':
      return 'Suspended';
    case 'refunded':
      return 'Refunded';
    case 'expired':
      return 'Expired';
    case 'active':
    default:
      return 'Active';
  }
}

export default function LicenseSummaryTable({
  customerName,
  issued,
  updatedDate,
  verifyUrl,
  status = 'active',
  onCopyLink,
}: LicenseSummaryTableProps) {
  return (
    <>
      <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>License summary</p>
      <div
        style={{
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          background: c.surface1,
          marginBottom: 18,
        }}
      >
        <SummaryRow label="License holder" value={customerName} />
        <SummaryRow label="Plan" value={COMMERCIAL_ADDON.name} />
        <SummaryRow label="License status" value={statusLabel(status)} />
        <SummaryRow label="License type" value="Lifetime Commercial License" />
        <SummaryRow label="First issued" value={issued} />
        <SummaryRow label="Last updated" value={updatedDate || '—'} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '9px 14px',
          }}
        >
          <span style={{ fontSize: 12, color: c.text3 }}>Verification URL</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
              {verifyUrl.replace(/^https?:\/\//, '')}
            </span>
            <button
              type="button"
              onClick={onCopyLink}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              aria-label="Copy verification link"
            >
              <Copy size={12} color={c.text3} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
